import { Component } from '@theme/component';

/**
 * Barre « livraison offerte » en haut du tiroir panier.
 *
 * Le tiroir est une section Horizon sans réglages ni blocks : impossible d'y
 * déclarer quoi que ce soit. L'élément vit donc ailleurs dans la page (pied de
 * page) et sert de contrôleur : il fabrique un noeud qu'il pose en tête du
 * tiroir, et le repose chaque fois qu'Horizon re-rend la section.
 *
 * @extends {Component}
 */
class LrsShippingBar extends Component {
  /** @type {AbortController | undefined} */
  #listeners;
  /** @type {MutationObserver | undefined} */
  #observer;
  connectedCallback() {
    super.connectedCallback();
    this.#attach();
    this.#listen();
    this.#watch();
    this.refresh();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#listeners?.abort();
    this.#observer?.disconnect();
  }

  /** Seuil en unité monétaire. 0 ou absent : la barre ne s'affiche pas. */
  get #threshold() {
    return Number(this.dataset.threshold || 0);
  }

  get #drawerInner() {
    return document.querySelector('.cart-drawer__inner');
  }

  #buildBar() {
    const bar = document.createElement('div');
    bar.className = 'lrs-shipping-bar';
    bar.innerHTML =
      '<p class="lrs-shipping-bar__text" aria-live="polite"></p>' +
      '<div class="lrs-shipping-bar__track"><div class="lrs-shipping-bar__fill"></div></div>';
    return bar;
  }

  /**
   * Aucune référence de noeud conservée : Horizon re-rend le tiroir par morphing
   * du DOM, une référence gardée devient silencieusement périmée. On interroge
   * le tiroir à chaque fois et on ne fabrique la barre que si elle manque.
   *
   * @returns {HTMLElement | null}
   */
  #attach() {
    const inner = this.#drawerInner;
    if (!inner || this.#threshold <= 0) return null;

    let bar = /** @type {HTMLElement | null} */ (inner.querySelector(':scope > .lrs-shipping-bar'));
    if (!bar) {
      bar = this.#buildBar();
      // SOUS l'en-tete, pas au-dessus. `prepend` la posait avant le titre du
      // tiroir : la premiere chose lue devenait une condition de livraison, et
      // le client ne voyait meme plus le nom de ce qu'il ouvrait. La barre
      // commente le panier, elle ne l'annonce pas.
      const entete = inner.querySelector(':scope > .theme-drawer__header, :scope > header');
      if (entete) entete.after(bar);
      else inner.prepend(bar);
    }
    return bar;
  }

  /** Contrat public : l'évènement standard de mise à jour du panier. */
  #listen() {
    this.#listeners = new AbortController();
    document.addEventListener('shopify:cart:lines-update', () => this.refresh(), {
      signal: this.#listeners.signal,
    });
  }

  /**
   * L'évènement de panier précède le remplacement du HTML du tiroir par la
   * Section Rendering API, et ce remplacement efface la barre. On surveille donc
   * la section et on repose dès que la barre a disparu. Le garde sur
   * `isConnected` évite de réagir à notre propre insertion.
   */
  #watch() {
    const section = document.querySelector('[id*="cart-drawer-section"]');
    if (!section) return;

    this.#observer = new MutationObserver(() => {
      if (this.#drawerInner?.querySelector(':scope > .lrs-shipping-bar')) return;
      this.refresh();
    });

    this.#observer.observe(section, { childList: true, subtree: true });
  }

  async refresh() {
    if (this.#threshold <= 0) return;

    const root = window.Shopify?.routes?.root ?? '/';
    let cart;
    try {
      const response = await fetch(`${root}cart.js`);
      if (!response.ok) return;
      cart = await response.json();
    } catch {
      return;
    }

    const bar = this.#attach();
    if (!bar) return;

    const thresholdCents = this.#threshold * 100;
    const remaining = Math.max(0, thresholdCents - cart.total_price);
    const ratio = thresholdCents > 0 ? Math.min(1, cart.total_price / thresholdCents) : 1;

    const text = bar.querySelector('.lrs-shipping-bar__text');
    const fill = /** @type {HTMLElement | null} */ (bar.querySelector('.lrs-shipping-bar__fill'));

    if (text) {
      if (remaining > 0) {
        const amount = new Intl.NumberFormat(document.documentElement.lang || 'fr', {
          style: 'currency',
          currency: cart.currency,
          maximumFractionDigits: 2,
        }).format(remaining / 100);
        // Le montant est un noeud a part, pour pouvoir le mettre en gras sans
        // injecter de balisage : c'est le seul chiffre que l'oeil doit
        // attraper. Rien d'autre ne change au calcul.
        const modele = this.dataset.labelRemaining || '';
        const [avant, apres] = modele.split('%AMOUNT%');
        const fort = document.createElement('b');
        fort.className = 'lrs-shipping-bar__amount';
        fort.textContent = amount;
        text.replaceChildren(
          document.createTextNode(avant || ''),
          fort,
          document.createTextNode(apres === undefined ? '' : apres)
        );
        bar.dataset.reached = 'false';
      } else {
        text.textContent = '';
        const check = document.createElement('span');
        check.className = 'lrs-shipping-bar__check';
        check.setAttribute('aria-hidden', 'true');
        check.textContent = '✓';
        text.append(check, document.createTextNode(this.dataset.labelReached || ''));
        bar.dataset.reached = 'true';
      }
    }

    if (fill) fill.style.width = `${Math.round(ratio * 100)}%`;
  }
}

if (!customElements.get('lrs-shipping-bar')) {
  customElements.define('lrs-shipping-bar', LrsShippingBar);
}
